import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CookiesNotification from './CookiesNotification';

// Original behavior: prefer REACT_APP_BACKEND_URL, fallback to same-origin
// Enforce same-origin in live/preview to avoid proxy mismatch causing 503s
const BACKEND_URL = window.location.origin;
// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

function LoginPage({ onLogin, successMessage = '', onForgotPassword, onBack, initialLoginMode = true }) {
  const [isLogin, setIsLogin] = useState(initialLoginMode);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [localSuccessMessage, setLocalSuccessMessage] = useState('');
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  
  // Legal agreement states for registration
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptRisk, setAcceptRisk] = useState(false);

  // FIX 155: Check if registration is enabled
  useEffect(() => {
    const checkRegistration = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/system/settings/public`, { timeout: 5000 });
        const enabled = res.data?.settings?.registration_enabled;
        if (enabled === false) {
          setRegistrationEnabled(false);
          if (!isLogin) setIsLogin(true); // Force back to login if trying to signup
        }
      } catch (e) {
        // If check fails, allow registration (fail-open)
      }
    };
    checkRegistration();
    // eslint-disable-next-line
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (loading) {
      return;
    }
    
    setError('');
    setLocalSuccessMessage('');
    
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    // Validate legal agreements for registration
    if (!isLogin && !acceptTerms) {
      setError('You must accept the Terms of Service to create an account.');
      return;
    }
    
    if (!isLogin && !acceptRisk) {
      setError('You must acknowledge the Risk Disclaimer to use our trading service.');
      return;
    }
    
    setLoading(true);
    
    try {
      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
      const response = await axios.post(`${BACKEND_URL}${endpoint}`, {
        email,
        password
      }, { timeout: 60000 });

      if (isLogin) {
        // Handle successful login
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify({
          user_id: response.data.user_id,
          email: response.data.email || email,
          subscription_active: response.data.subscription_active || false,
          is_admin: response.data.is_admin || false,
          admin_role: response.data.admin_role || null,
          role: response.data.role || null
        }));

        // Call parent callback
        onLogin({
          token: response.data.token,
          user: {
            user_id: response.data.user_id,
            email: response.data.email || email,
            subscription_active: response.data.subscription_active || false,
            is_admin: response.data.is_admin || false,
            admin_role: response.data.admin_role || null,
            role: response.data.role || null
          }
        });
      } else {
        // Handle registration success - show email verification message
        const successMessage = response.data.message || 'Registration successful! Please check your email to verify your account before logging in.';
        setLocalSuccessMessage(successMessage);
        setIsLogin(true); // Switch to login mode after registration
      }

    } catch (err) {
      try {
        const errorResponse = err.response?.data;
        
        if (errorResponse && typeof errorResponse === 'object') {
          // FastAPI wraps errors in {detail: {message, error_type}} or {detail: "string"}
          const detail = errorResponse.detail;
          const detailObj = (detail && typeof detail === 'object') ? detail : null;
          const errorType = detailObj?.error_type || errorResponse.error_type;
          
          if (errorType === "email_verification_required") {
            const msg = detailObj?.message || 'Please verify your email address before logging in.';
            setError(msg);
          } else {
            // Extract error message, handling nested detail objects
            const errorMessage = detailObj?.message || (typeof detail === 'string' ? detail : null) || errorResponse.message || 'Authentication failed';
            setError(typeof errorMessage === 'string' ? errorMessage : 'Authentication failed');
          }
        } else {
          setError('Authentication failed. Please try again.');
        }
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        setError('Authentication failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    setShowForgotPassword(true);
  };

  const handleForgotPasswordBack = () => {
    setShowForgotPassword(false);
  };

  const handleForgotPasswordSuccess = (message) => {
    setLocalSuccessMessage(message);
    setShowForgotPassword(false);
  };

  // Show forgot password form
  if (showForgotPassword) {
    return (
      <ForgotPasswordForm 
        onBack={handleForgotPasswordBack}
        onSuccess={handleForgotPasswordSuccess}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h1 className="text-center text-4xl font-bold text-white mb-2">
            🤖 Grid Trading Bot
          </h1>
          <h2 className="text-center text-2xl font-bold text-gray-300">
            {isLogin ? 'Sign in to your account' : 'Create your account'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-400">
            Multi-user trading platform with automated strategies
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-600 text-white p-3 rounded-lg text-sm">
              {error}
            </div>
          )}
          
          {(successMessage || localSuccessMessage) && (
            <div className="bg-green-600 text-white p-3 rounded-lg text-sm">
              {successMessage || localSuccessMessage}
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none relative block w-full px-3 py-3 border border-gray-600 placeholder-gray-400 text-white bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your email"
                data-testid="auth-email-input"
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="appearance-none relative block w-full px-3 py-3 border border-gray-600 placeholder-gray-400 text-white bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your password"
                minLength="6"
                data-testid="auth-password-input"
              />
            </div>
          </div>

          {/* Legal Agreement Checkboxes for Registration */}
          {!isLogin && (
            <div className="space-y-4">
              <div className="bg-yellow-900 border border-yellow-700 p-4 rounded-lg">
                <p className="text-yellow-100 text-sm font-semibold mb-3">
                  ⚠️ Legal Requirements
                </p>
                
                <div className="space-y-3">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      required
                      data-testid="accept-terms-checkbox"
                    />
                    <span className="text-yellow-100 text-sm">
                      I agree to the{' '}
                      <button
                        type="button"
                        onClick={() => window.open('/terms', '_blank')}
                        className="text-blue-400 hover:text-blue-300 underline"
                      >
                        Terms of Service
                      </button>{' '}
                      and{' '}
                      <button
                        type="button"
                        onClick={() => window.open('/privacy', '_blank')}
                        className="text-blue-400 hover:text-blue-300 underline"
                      >
                        Privacy Policy
                      </button>
                    </span>
                  </label>

                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={acceptRisk}
                      onChange={(e) => setAcceptRisk(e.target.checked)}
                      className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                      required
                      data-testid="accept-risk-checkbox"
                    />
                    <span className="text-yellow-100 text-sm">
                      I understand that trading involves substantial risk of loss and I have read the{' '}
                      <button
                        type="button"
                        onClick={() => window.open('/risk-disclaimer', '_blank')}
                        className="text-red-400 hover:text-red-300 underline font-semibold"
                      >
                        Risk Disclaimer
                      </button>
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="auth-submit-button"
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                  Processing...
                </span>
              ) : (
                isLogin ? 'Sign in' : 'Create account'
              )}
            </button>
          </div>

          <div className="text-center space-y-2">
            {registrationEnabled ? (
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-blue-400 hover:text-blue-300 text-sm block"
                data-testid="toggle-auth-mode-button"
              >
                {isLogin 
                  ? "Don't have an account? Sign up" 
                  : "Already have an account? Sign in"
                }
              </button>
            ) : (
              !isLogin ? (
                <button
                  type="button"
                  onClick={() => setIsLogin(true)}
                  className="text-blue-400 hover:text-blue-300 text-sm block"
                >
                  Already have an account? Sign in
                </button>
              ) : (
                <p className="text-gray-500 text-sm">Registration is currently closed</p>
              )
            )}
            
            {isLogin && (
              <button
                type="button"
                onClick={handleForgotPassword}
                className="text-gray-400 hover:text-gray-300 text-sm block"
                data-testid="forgot-password-button"
              >
                Forgot your password?
              </button>
            )}
            
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="text-gray-500 hover:text-gray-400 text-sm block mt-4 flex items-center justify-center mx-auto"
              >
                <span className="mr-1">←</span>
                Back to Home
              </button>
            )}
          </div>
        </form>
        
        <div className="mt-6 text-center text-xs text-gray-500">
          <p>Version 2.9.0 • Secure authentication</p>
        </div>
      </div>
      
      {/* Cookies Notification */}
      <CookiesNotification />
    </div>
  );
}

// Simple forgot password form component
function ForgotPasswordForm({ onBack, onSuccess }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, {
        email
      });

      onSuccess(`Reset link sent! Check your email (${email}) for password reset instructions.`);
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h1 className="text-center text-4xl font-bold text-white mb-2">
            🔑 Forgot Password
          </h1>
          <p className="mt-2 text-center text-sm text-gray-400">
            Enter your email address and we'll send you a reset link
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-600 text-white p-3 rounded-lg text-sm">
              {error}
            </div>
          )}
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
              Email address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="appearance-none relative block w-full px-3 py-3 border border-gray-600 placeholder-gray-400 text-white bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your email"
              data-testid="forgot-password-email-input"
            />
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="forgot-password-submit-button"
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                  Sending...
                </span>
              ) : (
                'Send Reset Link'
              )}
            </button>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={onBack}
              className="text-blue-400 hover:text-blue-300 text-sm"
              data-testid="forgot-password-back-button"
            >
              Back to Sign In
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default LoginPage;